import React, { useState, useEffect, useMemo } from 'react';
import { Charge } from '../core/_models';
import { getChargesPagination, cancelCharge, refundCharge } from '../core/subscriptionService';
import { ChargeRefundModal } from './ChargeRefundModal';
import { ChargeCancelModal } from './ChargeCancelModal';
import { StandaloneChargeModal } from './StandaloneChargeModal';
import { useToast } from '../../components/Toats';
import { PaginationGlobal } from '../../components/PaginationGlobal';
import Select from 'react-select';
import { getClientAll } from '../../../services/clientService';

export const ChargesTab: React.FC = () => {
  const [charges, setCharges] = useState<Charge[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCharge, setSelectedCharge] = useState<Charge | null>(null);
  const [showRefund, setShowRefund] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showStandalone, setShowStandalone] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { toggleToast } = useToast();

  const [pagination, setPagination] = useState({
    page: 1, limit: 50, total: 0, qtdPages: 0
  });
  const [qtd, setQtd] = useState({ start: 0, end: 0 });

  const [filters, setFilters] = useState({
    status: '',
    payment_method: '',
    client_id: '',
    reference_type: '',
    due_date_start: '',
    due_date_end: ''
  });
  const [clients, setClients] = useState<any[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    getClientAll().then(res => {
      setClients(res?.data || res || []);
    }).catch(() => setClients([]));
  }, []);



  const fetchCharges = () => {
    setLoading(true);
    getChargesPagination(pagination.limit, pagination.page, filters).then((data) => {
      setCharges(data.charges || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        qtdPages: data.lastPage || Math.ceil((data.total || 0) / prev.limit) || 1
      }));
      setLoading(false);
    }).catch(() => {
      setCharges([]);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchCharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    const start = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total);
    setQtd({ start, end });
  }, [pagination.page, pagination.limit, pagination.total]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PAGA':
        return <span className='badge badge-light-success fw-bolder px-4 py-3'>Paga</span>;
      case 'PENDENTE':
        return <span className='badge badge-light-warning fw-bolder px-4 py-3'>Pendente</span>;
      case 'VENCIDA':
        return <span className='badge badge-light-danger fw-bolder px-4 py-3'>Vencida</span>;
      case 'ESTORNADA':
        return <span className='badge badge-light-dark fw-bolder px-4 py-3'>Estornada</span>;
      case 'CANCELADA':
        return <span className='badge badge-light-secondary text-gray-600 fw-bolder px-4 py-3'>Cancelada</span>;
      default:
        return <span className='badge badge-light-primary fw-bolder px-4 py-3'>{status}</span>;
    }
  };

  const getTypeBadge = (referenceType: string) => {
    if (referenceType === 'AVULSA') {
      return <span className='badge badge-light-warning fw-bolder px-4 py-3'>Avulsa</span>;
    }
    return <span className='badge badge-light-primary fw-bolder px-4 py-3'>Assinatura</span>;
  };

  const formatInstallment = (charge: Charge) => {
    if (charge.installment_number && charge.subscription?.total_installments && charge.subscription.total_installments > 1) {
      return `${charge.installment_number}/${charge.subscription.total_installments}`;
    }
    return '—';
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'BOLETO':
        return <i className='bi bi-upc-scan fs-2x text-primary' title='Boleto'></i>;
      case 'PIX':
        return <i className='bi bi-qr-code text-success fs-2x' title='Pix'></i>;
      case 'CARTAO':
        return <i className='bi bi-credit-card fs-2x text-info' title='Cartão de Crédito'></i>;
      default:
        return <i className='bi bi-wallet2 fs-2x text-gray-600' title={method || 'Múltiplos'}></i>;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className='card card-flush h-md-100'>
      <div className='card-header pt-7'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder text-gray-800'>Histórico de Cobranças</span>
          <span className='text-gray-400 mt-1 fw-bold fs-6'>Acompanhe as faturas geradas</span>
        </h3>
        <div className='card-toolbar'>
          <div className='d-flex align-items-center gap-3'>
            <button
              className='btn btn-sm btn-light-primary fw-bolder'
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className='bi bi-funnel me-1'></i>
              Filtros
            </button>
            <button
              className='btn btn-sm btn-primary fw-bolder'
              onClick={() => setShowStandalone(true)}
            >
              <i className='bi bi-plus-lg me-1'></i>
              Cobrança Avulsa
            </button>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className='card-body border-top pt-5 pb-5'>
          <div className='row g-4'>
            <div className='col-md-3'>
              <label className='fs-6 fw-bold mb-2'>Status</label>
              <select 
                className='form-select form-select-solid'
                value={filters.status}
                onChange={e => { setPagination(prev => ({ ...prev, page: 1 })); setFilters({...filters, status: e.target.value}) }}
              >
                <option value=''>Todos</option>
                <option value='PENDENTE'>Pendente</option>
                <option value='PAGA'>Paga</option>
                <option value='VENCIDA'>Vencida</option>
                <option value='CANCELADA'>Cancelada</option>
                <option value='ESTORNADA'>Estornada</option>
              </select>
            </div>
            <div className='col-md-3'>
              <label className='fs-6 fw-bold mb-2'>Tipo</label>
              <select 
                className='form-select form-select-solid'
                value={filters.reference_type}
                onChange={e => { setPagination(prev => ({ ...prev, page: 1 })); setFilters({...filters, reference_type: e.target.value}) }}
              >
                <option value=''>Todos</option>
                <option value='ASSINATURA'>Assinatura</option>
                <option value='AVULSA'>Avulsa</option>
              </select>
            </div>
            <div className='col-md-3'>
              <label className='fs-6 fw-bold mb-2'>Método de Pagamento</label>
              <select 
                className='form-select form-select-solid'
                value={filters.payment_method}
                onChange={e => { setPagination(prev => ({ ...prev, page: 1 })); setFilters({...filters, payment_method: e.target.value}) }}
              >
                <option value=''>Todos</option>
                <option value='BOLETO'>Boleto</option>
                <option value='PIX'>Pix</option>
                <option value='CARTAO'>Cartão de Crédito</option>
              </select>
            </div>
            <div className='col-md-3'>
              <label className='fs-6 fw-bold mb-2'>Cliente</label>
              <Select
                options={clients.map(c => ({ value: c.id, label: c.nameResponsible || c.name || 'Sem nome' }))}
                value={filters.client_id ? { value: filters.client_id, label: clients.find(c => c.id === filters.client_id)?.nameResponsible || clients.find(c => c.id === filters.client_id)?.name || 'Cliente' } : null}
                onChange={(val: any) => { setPagination(prev => ({ ...prev, page: 1 })); setFilters({...filters, client_id: val?.value || ''}) }}
                placeholder='Todos os clientes'
                isClearable
                className='react-select-container'
                classNamePrefix='react-select'
              />
            </div>
            <div className='col-md-3'>
              <label className='fs-6 fw-bold mb-2'>Vencimento (Início)</label>
              <input 
                type='date' 
                className='form-control form-control-solid' 
                value={filters.due_date_start}
                onChange={e => { setPagination(prev => ({ ...prev, page: 1 })); setFilters({...filters, due_date_start: e.target.value}) }}
              />
            </div>
            <div className='col-md-3'>
              <label className='fs-6 fw-bold mb-2'>Vencimento (Fim)</label>
              <input 
                type='date' 
                className='form-control form-control-solid' 
                value={filters.due_date_end}
                onChange={e => { setPagination(prev => ({ ...prev, page: 1 })); setFilters({...filters, due_date_end: e.target.value}) }}
              />
            </div>
            <div className='col-md-6 d-flex align-items-end justify-content-end'>
              <button 
                className='btn btn-light me-2'
                onClick={() => {
                  setPagination(prev => ({ ...prev, page: 1 }));
                  setFilters({
                    status: '',
                    payment_method: '',
                    client_id: '',
                    reference_type: '',
                    due_date_start: '',
                    due_date_end: ''
                  });
                }}
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      )}
      <div className='card-body pt-6'>
        <div className='table-responsive'>
          <table className='table table-row-dashed align-middle gs-0 gy-4'>
            <thead>
              <tr className='text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0'>
                <th className='min-w-50px'>Método</th>
                <th className='min-w-200px'>Cliente</th>
                <th className='min-w-80px'>Tipo</th>
                <th className='min-w-80px'>Origem</th>
                <th className='min-w-80px'>Parcela</th>
                <th className='min-w-100px'>Valor</th>
                <th className='min-w-100px'>Vencimento</th>
                <th className='min-w-100px'>Status</th>
                <th className='text-end min-w-100px'>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className='text-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </td>
                </tr>
              ) : charges.length === 0 ? (
                <tr>
                  <td colSpan={9} className='text-center py-10 text-muted'>
                    Nenhuma cobrança encontrada. Ajuste os filtros.
                  </td>
                </tr>
              ) : (
                charges.map((charge) => (
                  <tr key={charge.id}>
                    <td>
                      <div className='d-flex align-items-center justify-content-center w-40px h-40px bg-light rounded'>
                        {getMethodIcon(charge.payment_method)}
                      </div>
                    </td>
                    <td>
                      <a href='#' className='text-gray-800 fw-bolder text-hover-primary mb-1 fs-6'>
                        {charge.client?.user?.name || charge.client?.nameResponsible || charge.client_name || 'Desconhecido'}
                      </a>
                    </td>
                    <td>
                      {getTypeBadge(charge.reference_type)}
                    </td>
                    <td>
                      {charge.service ? (
                        <span className='badge badge-light-primary fw-bolder px-4 py-3'>
                          OS #{charge.service.number_order_service}
                        </span>
                      ) : charge.proposal ? (
                        <span className='badge badge-light-info fw-bolder px-4 py-3'>
                          Proposta
                        </span>
                      ) : (
                        <span className='text-muted fs-7'>—</span>
                      )}
                    </td>
                    <td>
                      <span className='text-gray-800 fw-bolder fs-6'>
                        {formatInstallment(charge)}
                      </span>
                    </td>
                    <td>
                      <span className='text-gray-800 fw-bolder fs-6'>
                        R$ {Number(charge.gross_value).toFixed(2).replace('.', ',')}
                      </span>
                    </td>
                    <td>
                      <span className='text-gray-800 fw-bolder fs-6'>{formatDate(charge.due_date)}</span>
                    </td>
                    <td>{getStatusBadge(charge.status)}</td>
                    <td className='text-end'>
                      <div className='btn-group' role='group'>
                        <button type='button' className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm' title='Ver Fatura' onClick={() => window.open(charge.invoice_link, '_blank')}>
                          <i className='bi bi-file-earmark-text fs-4'></i>
                        </button>
                        {charge.status === 'PAGA' && (
                          <button type='button' className='btn btn-icon btn-bg-light btn-active-color-warning btn-sm' title='Estornar' onClick={(e) => { e.preventDefault(); setSelectedCharge(charge); setShowRefund(true); }}>
                            <i className='bi bi-arrow-counterclockwise fs-4'></i>
                          </button>
                        )}
                        {(charge.status === 'PENDENTE' || charge.status === 'VENCIDA') && (
                          <button type='button' className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title='Cancelar Fatura' onClick={(e) => { e.preventDefault(); setSelectedCharge(charge); setShowCancel(true); }}>
                            <i className='bi bi-x-circle fs-4'></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className='card-footer pt-1 border-0'>
        <PaginationGlobal 
            pagination={pagination}
            setPagination={setPagination}
            qtd={qtd}
        />
      </div>

      <ChargeRefundModal
        charge={selectedCharge}
        show={showRefund}
        isLoading={isActionLoading}
        onHide={() => { setShowRefund(false); setSelectedCharge(null); }}
        onConfirm={(id, reason) => {
          setIsActionLoading(true);
          refundCharge(id, reason).then(() => {
            toggleToast('Estorno solicitado com sucesso', 'success');
            fetchCharges();
            setShowRefund(false);
            setSelectedCharge(null);
          }).catch((err) => {
            toggleToast(err.response?.data?.error || 'Erro ao estornar cobrança', 'danger');
          }).finally(() => {
            setIsActionLoading(false);
          });
        }}
      />
      <ChargeCancelModal
        charge={selectedCharge}
        show={showCancel}
        isLoading={isActionLoading}
        onHide={() => { setShowCancel(false); setSelectedCharge(null); }}
        onConfirm={(id) => {
          setIsActionLoading(true);
          cancelCharge(id).then(() => {
            toggleToast('Cobrança cancelada com sucesso', 'success');
            fetchCharges();
            setShowCancel(false);
            setSelectedCharge(null);
          }).catch((err) => {
            toggleToast(err.response?.data?.error || 'Erro ao cancelar cobrança', 'danger');
          }).finally(() => {
            setIsActionLoading(false);
          });
        }}
      />
      <StandaloneChargeModal
        show={showStandalone}
        onHide={() => setShowStandalone(false)}
        onSuccess={() => {
          setShowStandalone(false);
          fetchCharges();
        }}
      />
    </div>
  );
};
